import { useCallback, useEffect, useState } from "react";
import { Check, Star, Trash2 } from "lucide-react";
import { adminApproveReview, adminDeleteReview, adminGetReviews } from "@/lib/api";
import { toast } from "@/components/ui/sonner";

const Stars = ({ n }) => (
  <span className="flex gap-0.5">
    {[1, 2, 3, 4, 5].map((i) => (
      <Star key={i} className={`w-3.5 h-3.5 ${i <= n ? "fill-amber-400 text-amber-400" : "text-muted-foreground/30"}`} />
    ))}
  </span>
);

export const ReviewsTab = ({ token }) => {
  const [reviews, setReviews] = useState([]);

  const load = useCallback(async () => {
    try {
      setReviews(await adminGetReviews(token));
    } catch {
      toast.error("Could not load reviews");
    }
  }, [token]);

  useEffect(() => {
    load();
  }, [load]);

  const approve = async (r, approved) => {
    await adminApproveReview(token, r.id, approved);
    setReviews((prev) => prev.map((x) => (x.id === r.id ? { ...x, approved } : x)));
    toast.success(approved ? "Review is now live" : "Review hidden");
  };

  const remove = async (r) => {
    if (!window.confirm("Delete this review permanently?")) return;
    await adminDeleteReview(token, r.id);
    setReviews((prev) => prev.filter((x) => x.id !== r.id));
    toast.success("Review deleted");
  };

  const pending = reviews.filter((r) => !r.approved);
  const live = reviews.filter((r) => r.approved);

  return (
    <div className="space-y-8" data-testid="reviews-tab">
      <div>
        <p className="text-xs font-bold uppercase tracking-[0.15em] text-accent mb-3">Awaiting approval ({pending.length})</p>
        {pending.length === 0 && <p className="text-sm text-muted-foreground">No pending reviews.</p>}
        <div className="space-y-3">
          {pending.map((r) => (
            <div key={r.id} data-testid={`review-${r.id}`} className="bg-amber-50/60 border border-amber-200/60 rounded-xl p-5">
              <div className="flex flex-wrap justify-between gap-3">
                <div>
                  <p className="font-semibold text-sm">{r.name} <span className="text-muted-foreground font-normal">on {r.product_slug}</span></p>
                  <Stars n={r.rating} />
                  {r.title && <p className="font-semibold text-sm mt-2">{r.title}</p>}
                  <p className="text-sm text-muted-foreground mt-1">{r.text}</p>
                  <p className="text-xs text-muted-foreground mt-1.5">Order {r.order_no}</p>
                </div>
                <div className="flex gap-2 h-fit">
                  <button
                    data-testid={`review-approve-${r.id}`}
                    onClick={() => approve(r, true)}
                    className="rounded-full bg-primary text-primary-foreground px-4 py-2 text-xs font-bold flex items-center gap-1.5"
                  >
                    <Check className="w-3.5 h-3.5" /> Approve
                  </button>
                  <button
                    data-testid={`review-delete-${r.id}`}
                    onClick={() => remove(r)}
                    className="rounded-full border border-border px-4 py-2 text-xs font-bold text-muted-foreground hover:text-destructive hover:border-destructive/40 flex items-center gap-1.5 transition-colors"
                  >
                    <Trash2 className="w-3.5 h-3.5" /> Delete
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div>
        <p className="text-xs font-bold uppercase tracking-[0.15em] text-primary mb-3">Live on store ({live.length})</p>
        {live.length === 0 && <p className="text-sm text-muted-foreground">No approved reviews yet.</p>}
        <div className="space-y-3">
          {live.map((r) => (
            <div key={r.id} className="bg-white/70 border border-border/60 rounded-xl p-5 flex flex-wrap justify-between gap-3">
              <div>
                <p className="font-semibold text-sm">{r.name} <span className="text-muted-foreground font-normal">on {r.product_slug}</span></p>
                <Stars n={r.rating} />
                <p className="text-sm text-muted-foreground mt-1">{r.title ? `${r.title} — ` : ""}{r.text}</p>
              </div>
              <div className="flex gap-2 h-fit">
                <button
                  data-testid={`review-hide-${r.id}`}
                  onClick={() => approve(r, false)}
                  className="rounded-full border border-border px-4 py-2 text-xs font-bold hover:bg-secondary transition-colors"
                >
                  Hide
                </button>
                <button
                  onClick={() => remove(r)}
                  className="rounded-full border border-border px-4 py-2 text-xs font-bold text-muted-foreground hover:text-destructive hover:border-destructive/40 transition-colors"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
